import React, { useCallback } from 'react';
import Image from 'js/components/common/Image';
import { compose, omit, path, pick, prop, identity } from 'ramda';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { useAppSelector, useDeviceSelector, useUserSelector } from 'js/store/hooks';
import { branch, mapProps, pure, renderNothing, withHandlers, withProps } from 'recompose';

import FeatureIcons from './FeatureIcons';
import { FavoriteIcon } from './FavoriteIcon';
import withGameLaunch from './withGameLaunch';
import { withGameInfo } from './withGameInfo';

import style from './Game.scss';

export const GameBare = ({
    t,
    code,
    name,
    images = {},
    size = 'extraSmall',
    isRelativeSize = false,
    showTitle = false,
    showFeatureIcons = true,
    gameFeed,
    imageClassName,
    showFavoriteIcon,
    showRecentlyPlayedIcon,
    className = '',
    isLoggedIn,
    setGameRef,
    setGameImageRef,
    onClick,
    onMouseMove,
    isNew,
    hot,
    popular,
    exclusive,
    hideButtons,
    isGameIsNotEarnPoints,
    isGameFromEndedRule,
    isInH2SPages
}) => {
    const onKeyDown = useCallback((event) => {
        const { key, target, currentTarget } = event;

        if (key === 'Enter' && target === currentTarget) {
            event.preventDefault();

            onClick?.(event);
        }
    }, [onClick]);

    return (
        <div
            tabIndex="0"
            ref={setGameRef}
            onClick={onClick}
            onKeyDown={onKeyDown}
            data-game-code={code}
            className={`${style.container} ${imageClassName}
     ${isRelativeSize ? '' : style[size]} ${className}`}
            aria-label={`${t('playtech.mobile.system.game.aria-label')} ${name}`}
        >
            <div
                aria-hidden={true}
                ref={setGameImageRef}
                onMouseMove={onMouseMove}
                className={`${style.imageContainer} ${isInH2SPages && style.imageContainerInH2S} ${hideButtons ? style.disableCursor : ''}`}
            >
                <Image src={!isLoggedIn && images.guest || images.base}
                   className={`${style.image} ${isGameIsNotEarnPoints || isGameFromEndedRule ? [style.grayedOut] : ''}`} alt={name}/>
                {(showFeatureIcons || showFavoriteIcon) && <div className={style.background} />}
                {showRecentlyPlayedIcon && <div className={style.recentlyPlayedIcon} />}
            </div>
            {showFeatureIcons && (
                <div className={style.labels} aria-hidden={true}>
                    {isNew && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.new')}</span>}
                    {popular && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.popular')}</span>}
                    {hot && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.hot')}</span>}
                    {exclusive && <span className={style.label}>{t('playtech.mobile.responsivegames.game.label.exclusive')}</span>}
                </div>
            )}
            <div className={style.sideIcons}>
                {showFeatureIcons && (
                    <FeatureIcons
                        code={code}
                        noContainer={true}
                        aria-hidden={true}
                        classNames={{ item: style.featured }}
                        types={['volatility', 'goldenChips', 'wilds', 'fsb']}
                    />
                )}
                <div className={style.spacer} />
                {!showTitle && showFavoriteIcon && (
                    <FavoriteIcon
                        code={code}
                        gameFeed={gameFeed}
                        className={style.favorite}
                    />
                )}
            </div>
            {showTitle && (
                <div className={style.titleWrapper}>
                    <span className={`${style.name} ${style.ellipsis}`} aria-hidden={true}>{name}</span>
                    {showFavoriteIcon && (
                        <FavoriteIcon
                            code={code}
                            isFeatured={true}
                            gameFeed={gameFeed}
                            className={style.favorite}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export const isGameAvailable = compose(
    withProps(({ code }) => ({ isAvailable: Boolean(useAppSelector(path(['games', code]))) })),
    branch(
        props => !props.isAvailable,
        renderNothing
    ),
    mapProps(omit(['isAvailable']))
);

export const enhance = compose(
    pure,
    withTranslations,
    isGameAvailable,
    branch(
        ({ showGameInfo }) => showGameInfo,
        withGameInfo,
        withGameLaunch
    ),
    withHandlers(({ launchGame, openGameInfo, disableGameLaunch }) => ({
        onClick: () => (event) => {
            const onGameClick = disableGameLaunch ? identity : launchGame || openGameInfo;

            event.preventDefault();
            onGameClick();
        }
    })),
    withProps(({ code }) => ({
        isDesktop: useDeviceSelector(prop('isDesktop')),
        ...useAppSelector(path(['games', code])),
        ...useUserSelector(pick(['isLoggedIn']))
    })),
    mapProps(omit(['description', 'funSupport', 'jackpotCode']))
);

export const Game = enhance(GameBare);
